import { db, withSyncWrite } from '../db/indexeddb'
import { getCloudTasksByIds, getCloudTagIds } from '../supabase/queries'
import { resolveLocalProjectId, resolveLocalSectionId } from './sync-resolve'
import { MAX_RETRIES } from './sync-queue'

/**
 * Repair & Cleanup Helpers
 * Post-sync maintenance operations.
 */

/**
 * Alle sync-fähigen Tabellen, unter dem Namen, den die `syncQueue` benutzt.
 *
 * Eine hier fehlende Tabelle fällt aus beiden Reparaturen unten heraus — und
 * das fällt nicht auf, weil alles andere weiter funktioniert. Neue Entity?
 * Dieser Eintrag ist Pflicht.
 */
function syncedTables() {
  return {
    tasks: db.tasks,
    subtasks: db.subtasks,
    timeEntries: db.timeEntries,
    taskComments: db.taskComments,
    projectComments: db.projectComments,
    taskReminders: db.taskReminders,
    sections: db.sections,
    tags: db.tags,
    projects: db.projects,
    goals: db.goals,
    habits: db.habits,
    habitLogs: db.habitLogs,
    rewardUsages: db.rewardUsages,
    userPreferences: db.userPreferences,
    canvasNotes: db.canvasNotes,
  } as Record<string, any>
}

/**
 * **Waisen anmelden**: lokale Zeilen ohne Cloud-Kennung, für die niemand einen
 * Push angemeldet hat.
 *
 * Der Push liest die `syncQueue`. Normalerweise stellt jede CRUD-Methode dort
 * ihren Eintrag (`db.createCanvasNote` → `add` + `queueSyncChange`), und der
 * Repair-Sweep in `pullRemoteEntities` (Schritt 4) heilt stale
 * `synced: false` — aber nur bei Zeilen, die schon eine `cloudId` tragen. Eine
 * Zeile OHNE `cloudId` und ohne Warteschlangen-Eintrag fällt durch beide
 * Netze: nie hochgeladen, und niemand trägt es nach. Sie steht dann für immer
 * nur auf diesem einen Gerät.
 *
 * So etwas entsteht überall dort, wo Zeilen an den CRUD-Methoden vorbei
 * geschrieben werden — ein Dexie-`modify` in einer Migration, ein Import, ein
 * abgebrochener Zyklus. Am 07.09.2026 waren es 184 canvasNotes aus den
 * Migrationen v28–v31, dazu ein Projekt, das im Web fehlte.
 *
 * **`cloudId`, nicht `synced`, ist das Kriterium.** Eine Zeile ohne
 * Cloud-Kennung steht nachweislich nicht oben, ganz gleich was ihr Flag
 * behauptet. Genau daran scheiterte der bestehende Pfad für Tags und Projekte
 * (`stageUnsyncedWithNameDedup`), der auf `!synced` filtert: Eine Waise, die
 * irgendwann auf `synced: true` gesetzt wurde, war für ihn unsichtbar.
 *
 * Doppelte Einträge sind ausgeschlossen — der Sweep läuft bei jedem Sync und
 * überspringt, was schon wartet. Ohne diese Prüfung liefe die Warteschlange
 * voll und der Push legte dieselbe Zeile mehrfach in der Cloud an.
 *
 * Gibt zurück, wie viele Zeilen angemeldet wurden.
 */
export async function stageOrphanedLocalRecords(): Promise<number> {
  let staged = 0
  try {
    /*
      Als angemeldet zählt NUR ein `create`, das der Push auch noch anfassen
      wird. Zwei Arten von Einträgen sehen aus wie ein Nachtrag und sind keiner:

      **Ein `update` legt nichts an.** `sync-push.ts` (case 'update') steigt
      aus, sobald die Zeile keine `cloudId` trägt — es wartet seinerseits auf
      ein `create`. Ein wartendes `update` als „ist angemeldet" zu zählen,
      hielte die Zeile für immer unten.

      **Ein AUSGEBRANNTER Eintrag wartet nicht — er ist tot.**
      `getPendingChanges` blendet alles mit `retryCount >= MAX_RETRIES`
      dauerhaft aus, und `retryFailedItems`, das ihn zurückholen würde, ruft
      niemand auf.

      Am 07.09.2026 lagen beide Fälle übereinander: 39 Notizen, deren `create`
      an einer fehlenden Cloud-Spalte ausgebrannt war, und darüber je ein
      frisches `update` aus dem normalen Bearbeiten. Der Push lief bei jedem
      Zyklus über die 39 Updates und tat nichts (`created:0, updated:0`), weil
      keine der Zeilen eine Cloud-Kennung hatte. Ein neuer `create` startet bei
      `retryCount: 0` — damit läuft es beim nächsten Zyklus von allein an.
    */
    const waiting = await db.syncQueue
      .filter(item =>
        !item.synced
        && item.action === 'create'
        && (item.retryCount ?? 0) < MAX_RETRIES)
      .toArray()
    const waitingByTable = new Map<string, Set<string>>()
    for (const item of waiting) {
      const localId = item.data?.localId as string | undefined
      if (!localId) continue
      let set = waitingByTable.get(item.table)
      if (!set) waitingByTable.set(item.table, (set = new Set()))
      set.add(localId)
    }

    for (const [name, table] of Object.entries(syncedTables())) {
      // Eine GELÖSCHTE Waise bleibt liegen: Sie war nie oben, es gibt dort
      // nichts zu löschen. Sie hochzuladen, um sie sofort wieder zu löschen,
      // wäre Unsinn — `purgeSyncedDeletions` räumt sie lokal weg.
      // **Der laufende Fokus ist keine Waise, er ist beschäftigt.** Ein Abschnitt
      // ohne Ende bleibt auf dem Gerät und geht erst hoch, wenn Pause oder Haken
      // ihn beenden (ADR 0025). Ohne diese Ausnahme meldete ihn der Sweep beim
      // nächsten Zyklus an — und das Web zeigte den Fokus eines Rechners, der
      // vielleicht längst abgestürzt ist.
      const orphans = await table
        .filter((r: any) => !r.cloudId && !r.deletedAt && !(name === 'timeEntries' && r.endTime === null))
        .toArray()
      if (orphans.length === 0) continue

      const pending = waitingByTable.get(name)
      for (const row of orphans) {
        if (pending?.has(row.id)) continue
        // `silent` wie beim Namens-Dedup: Wir stehen vor `getPendingChanges()`
        // im selben Zyklus, der Push nimmt den Eintrag mit (ADR 0017).
        await db.queueSyncChange('create', name as any, { localId: row.id }, { silent: true })
        staged++
      }
    }

    if (staged > 0) {
      // „eingereiht", nicht „angemeldet": **Anmelden** ist in dieser App das
      // Einloggen in HappyCloud (CONTEXT.md, Cloud user).
      console.log(`[Sync] ${staged} lokale Zeilen ohne Cloud-Kennung zum Push eingereiht`)
    }
  } catch (error) {
    console.warn('[Sync] stageOrphanedLocalRecords failed:', error)
  }
  return staged
}

/**
 * Hard-delete soft-deleted tasks/projects/sections that have already been synced to cloud.
 * Only removes records where deletedAt is set AND the deletion was pushed (no
 * pending 'delete' action in the sync queue for that record).
 */
export async function purgeSyncedDeletions(): Promise<void> {
  try {
    const pendingDeletes = await db.syncQueue
      .filter(item => item.action === 'delete' && !item.synced)
      .toArray()

    /*
      JEDE sync-fähige Tabelle gehört dazu — eine übersehene sammelt Leichen für
      immer. Die kosten nicht nur Platz: `pullRemoteEntities` vergleicht die
      Zahl der lokalen Zeilen mit `cloudId` gegen die Cloud-Zahl, und die zählt
      nur lebende. Tote Zeilen treiben die lokale Seite hoch, bis das Tor nie
      mehr aufgeht und bei jedem Zyklus der volle ID-Fetch läuft.

      Am 07.09.2026 in der Dev-App: 617 tote canvasNote-Zeilen, weil die Tabelle
      hier fehlte — dazu subtasks, habits, habitLogs, rewardUsages und
      userPreferences.
    */
    const tables = syncedTables()

    // Build pending-ID sets in a single pass
    const pendingIds: Record<string, Set<string>> = Object.fromEntries(
      Object.keys(tables).map(name => [name, new Set<string>()]),
    )
    for (const item of pendingDeletes) {
      const id = item.data?.localId as string
      if (id && item.table in pendingIds) pendingIds[item.table].add(id)
    }

    // Hard-delete synced soft-deleted records using bulkDelete (single transaction per table)
    for (const [name, table] of Object.entries(tables)) {
      const deleted = await table.filter((r: any) => r.deletedAt !== null).toArray()
      const pending = pendingIds[name]
      const idsToDelete = deleted.filter((r: any) => !pending.has(r.id)).map((r: any) => r.id)
      if (idsToDelete.length) await table.bulkDelete(idsToDelete)
    }
  } catch (error) {
    console.warn('[Sync] purgeSyncedDeletions failed:', error)
  }
}

/**
 * Repair tasks with null projectId/sectionId that should have associations.
 * Finds local tasks where cloudId is set but projectId or sectionId is null,
 * fetches their cloud data to get project_id/section_id, and resolves locally.
 * This fixes tasks that were pulled before their project/section existed locally.
 */
export async function repairOrphanedTaskAssociations(userId: string): Promise<{ repaired: number }> {
  let repaired = 0
  try {
    const allTasks = await db.tasks.filter(t => !!t.cloudId && !t.deletedAt).toArray()
    const orphaned = allTasks.filter(t => t.projectId === null || t.projectId === undefined)

    if (orphaned.length === 0) return { repaired }

    const orphanedCloudIds = orphaned.flatMap(t => t.cloudId ? [t.cloudId] : [])
    if (orphanedCloudIds.length === 0) return { repaired }

    const cloudTasks = await getCloudTasksByIds(userId, orphanedCloudIds)
    const cloudTaskMap = new Map(cloudTasks.map(ct => [ct.id, ct]))

    for (const task of orphaned) {
      const cloudTask = cloudTaskMap.get(task.cloudId!)
      if (!cloudTask) continue

      const updates: Record<string, any> = {}

      if (!task.projectId && cloudTask.project_id) {
        const localProjectId = await resolveLocalProjectId(cloudTask.project_id)
        if (localProjectId) updates.projectId = localProjectId
      }

      if (!task.sectionId && cloudTask.section_id) {
        const localSectionId = await resolveLocalSectionId(cloudTask.section_id)
        if (localSectionId) updates.sectionId = localSectionId
      }

      if (Object.keys(updates).length > 0) {
        await withSyncWrite(() =>
          db.tasks.update(task.id, { ...updates, synced: true, syncedAt: new Date() })
        )
        repaired++
      }
    }

    if (repaired > 0) {
      console.log(`[Sync] Repaired ${repaired} orphaned task associations (projectId/sectionId)`)
    }
  } catch (error) {
    console.warn('[Sync] repairOrphanedTaskAssociations failed:', error)
  }
  return { repaired }
}

/**
 * Remove local tags whose cloudId no longer exists in Supabase (hard-deleted in cloud).
 * Also re-maps any task references from the orphaned tag to a surviving tag with the same name.
 */
export async function purgeOrphanedCloudTags(userId: string): Promise<number> {
  try {
    const localTags = await db.tags.filter(t => !!t.cloudId && !t.deletedAt).toArray()
    if (localTags.length === 0) return 0

    const cloudTagIds = new Set(await getCloudTagIds(userId))
    const orphaned = localTags.filter(t => !cloudTagIds.has(t.cloudId!))
    if (orphaned.length === 0) return 0

    const survivingTags = localTags.filter(t => cloudTagIds.has(t.cloudId!))

    for (const orphan of orphaned) {
      // Try to re-map task references to a surviving tag with the same name
      const replacement = survivingTags.find(t => t.name.toLowerCase() === orphan.name.toLowerCase())
      if (replacement) {
        const tasksWithOrphan = await db.tasks.filter(t => t.tags.includes(orphan.id)).toArray()
        for (const task of tasksWithOrphan) {
          const updatedTags = task.tags.map(id => id === orphan.id ? replacement.id : id)
          await withSyncWrite(() => db.tasks.update(task.id, { tags: updatedTags }))
        }
      } else {
        // No replacement — just remove the orphan ID from tasks
        const tasksWithOrphan = await db.tasks.filter(t => t.tags.includes(orphan.id)).toArray()
        for (const task of tasksWithOrphan) {
          const updatedTags = task.tags.filter(id => id !== orphan.id)
          await withSyncWrite(() => db.tasks.update(task.id, { tags: updatedTags }))
        }
      }

      await withSyncWrite(() => db.tags.delete(orphan.id))
    }

    if (orphaned.length > 0) {
      console.log(`[Sync] Purged ${orphaned.length} orphaned cloud tag(s)`)
    }
    return orphaned.length
  } catch (error) {
    console.warn('[Sync] purgeOrphanedCloudTags failed:', error)
    return 0
  }
}
